import { Injectable } from '@angular/core';
import { ICommentModel } from '@fi-shared/models';
import { Observable, Subject } from 'rxjs';
import { ICommentActionData } from '../../fi-comments';
import { ObjectTypes } from '@fi-shared/types';
import { CommentsState } from './comments.state';
import { CommentsOperations } from './comments.operations';

@Injectable()
export class FiCommentsListStateService {
    private readonly state = new CommentsState();
    private readonly operations = new CommentsOperations(this.state);

    // State access methods
    public getCommentsCount(objectType: ObjectTypes, objectId: number): number {
        return this.state.getFlattenComments(objectType, objectId).length;
    }

    public getVisibleComments$(objectType: ObjectTypes, objectId: number): Observable<ICommentModel[]> {
        return this.state.getVisibleComments$(objectType, objectId);
    }

    public getVisibleComments(objectType: ObjectTypes, objectId: number): ICommentModel[] {
        return this.state.getVisibleComments(objectType, objectId);
    }

    public getComments$(objectType: ObjectTypes, objectId: number): Observable<ICommentModel[]> {
        return this.state.getComments$(objectType, objectId);
    }

    public getComments(objectType: ObjectTypes, objectId: number): ICommentModel[] {
        return this.state.getComments(objectType, objectId);
    }

    public getFlattenComments(objectType: ObjectTypes, objectId: number): ICommentModel[] {
        return this.state.getFlattenComments(objectType, objectId);
    }

    // Operations
    public setComments(
        comments: ICommentModel[],
        commentsToShowLimit: number,
        objectType: ObjectTypes,
        objectId: number,
    ): void {
        //fiExecuteOnItemsWithChildren(comments, (comment) => {});
        this.operations.setComments(comments, commentsToShowLimit, objectType, objectId);
    }

    public showAllComments(objectType: ObjectTypes, objectId: number): void {
        this.operations.showAllComments(objectType, objectId);
    }

    public handleRemoveComment(comment: ICommentModel, objectType: ObjectTypes, objectId: number): void {
        this.operations.handleRemoveComment(comment, objectType, objectId);
    }

    public handleUpsertComment(comment: ICommentModel, objectType: ObjectTypes, objectId: number): void {
        this.operations.handleUpsertComment(comment, objectType, objectId);
    }

    public getCommentById(id: number, objectType: ObjectTypes, objectId: number): ICommentModel | null {
        return this.operations.getCommentById(id, objectType, objectId);
    }

    public flattenCommentsChildren(comment: ICommentModel): ICommentModel[] {
        return this.operations.flattenCommentsChildren(comment);
    }

    // Action streams
    get deleteComment$(): Observable<ICommentActionData> {
        return this.state.deleteComment$;
    }

    get saveComment$(): Observable<ICommentActionData> {
        return this.state.saveComment$;
    }

    get commentsUpdated$(): Observable<number> {
        return this.state.commentsUpdated$;
    }

    // Action emitters
    emitDeleteComment(commentActionData: ICommentActionData): void {
        this.state.emitDeleteComment(commentActionData);
    }

    emitSaveComment(commentActionData: ICommentActionData): void {
        this.state.emitSaveComment(commentActionData);
    }

    private _commentUpdateConnectionLine$ = new Subject<{ objectType: ObjectTypes; objectId: number }>();
    emitCommentUpdateConnectionLine(objectType: ObjectTypes, objectId: number): void {
        this._commentUpdateConnectionLine$.next({ objectType, objectId });
    }

    get commentUpdateConnectionLine$(): Observable<{ objectType: ObjectTypes; objectId: number }> {
        return this._commentUpdateConnectionLine$.asObservable();
    }
}
